import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, CheckCircle2, Clock, Trash2, MoreVertical, Sparkles,
  Loader2, Tag, AlertTriangle, Edit2, MessageSquare, Filter, Users
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useTaskStore, useProjectStore, useUIStore, useAuthStore, useAdminStore } from '../lib/store';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import { format } from 'date-fns';
import TaskDetailDialog from '../components/TaskDetailDialog';

const TasksPage = () => {
  const { tasks, fetchTasks, createTask, updateTask, deleteTask, autoFillTask } = useTaskStore();
  const { projects, currentProject } = useProjectStore();
  const { language } = useUIStore();
  const { user: currentUser } = useAuthStore();
  const { users, fetchUsers } = useAdminStore();
  const { t } = useTranslation(language);
  
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [filter, setFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [showTaskDetail, setShowTaskDetail] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [assigneeType, setAssigneeType] = useState('self'); // 'self' or 'others'
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'todo',
    due_date: null,
    tags: [],
    project_id: currentProject?.id || '',
    assignee: null,
  });
  const [aiSuggestions, setAiSuggestions] = useState(null);

  // Check if user can assign to others (Admin, Project Lead, Project Manager)
  const canAssignToOthers = () => {
    if (!currentUser) return false;
    if (currentUser.is_admin) return true;
    // Check if user has project_lead or project_manager role in any project
    // For simplicity, we'll check project members later
    return currentUser.role === 'project_lead' || currentUser.role === 'project_manager';
  };

  // Check if user can reassign tasks
  const canReassignTask = () => {
    if (!currentUser) return false;
    return currentUser.is_admin || currentUser.role === 'project_lead' || currentUser.role === 'project_manager';
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        await fetchTasks();
        // Fetch users if user can assign to others
        if (currentUser?.is_admin || canAssignToOthers()) {
          await fetchUsers();
        }
      } catch (error) {
        toast.error(error.message || t('error'));
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (currentProject) {
      setNewTask(prev => ({ ...prev, project_id: currentProject.id }));
    }
  }, [currentProject?.id]);

  const handleAutoFill = async () => {
    if (!newTask.title.trim()) {
      toast.error(language === 'hi' ? 'पहले टाइटल दर्ज करें' : 'Enter a title first');
      return;
    }

    setIsAutoFilling(true);
    try {
      const suggestions = await autoFillTask(newTask.title, newTask.project_id);
      setAiSuggestions(suggestions);
      toast.success(language === 'hi' ? 'AI सुझाव मिले' : 'AI suggestions received');
    } catch (error) {
      toast.error(error.message || t('error'));
    } finally {
      setIsAutoFilling(false);
    }
  };

  const applyAiSuggestions = () => {
    if (aiSuggestions) {
      setNewTask(prev => ({
        ...prev,
        description: aiSuggestions.description || prev.description,
        priority: aiSuggestions.priority || prev.priority,
        due_date: aiSuggestions.due_date || prev.due_date,
        tags: aiSuggestions.tags || prev.tags,
      }));
      setAiSuggestions(null);
      toast.success(language === 'hi' ? 'सुझाव लागू हुए' : 'Suggestions applied');
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      toast.error(language === 'hi' ? 'टास्क टाइटल आवश्यक है' : 'Task title is required');
      return;
    }
    if (!newTask.project_id) {
      toast.error(language === 'hi' ? 'प्रोजेक्ट चुनें' : 'Select a project');
      return;
    }

    // Set assignee based on selection
    const taskData = {
      ...newTask,
      assignee: assigneeType === 'self' ? currentUser?.id : newTask.assignee
    };

    setIsCreating(true);
    try {
      await createTask(taskData);
      toast.success(language === 'hi' ? 'टास्क बनाया गया' : 'Task created');
      setShowCreateDialog(false);
      resetForm();
    } catch (error) {
      toast.error(error.message || t('error'));
    } finally {
      setIsCreating(false);
    }
  };

  const resetForm = () => {
    setAssigneeType('self');
    setNewTask({
      title: '',
      description: '',
      priority: 'medium',
      status: 'todo',
      due_date: null,
      tags: [],
      project_id: currentProject?.id || '',
      assignee: null,
    });
    setAiSuggestions(null);
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTask(taskId, { status: newStatus });
      toast.success(language === 'hi' ? 'टास्क अपडेट हुआ' : 'Task updated');
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask(taskId);
      toast.success(language === 'hi' ? 'टास्क हटाया गया' : 'Task deleted');
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const handleOpenTaskDetail = (task) => {
    setSelectedTask(task);
    setShowTaskDetail(true);
  };

  const handleTaskDetailSave = async (updatedTask) => {
    try {
      await updateTask(updatedTask.id, updatedTask);
      toast.success(language === 'hi' ? 'टास्क अपडेट हुआ' : 'Task updated');
      setShowTaskDetail(false);
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  const handleEditTask = (task) => {
    setEditingTask({ ...task });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editingTask) return;
    try {
      await updateTask(editingTask.id, editingTask);
      toast.success(language === 'hi' ? 'टास्क अपडेट हुआ' : 'Task updated');
      setShowEditDialog(false);
      setEditingTask(null);
    } catch (error) {
      toast.error(error.message || t('error'));
    }
  };

  // Get users visible based on role
  const getVisibleUsers = () => {
    if (!currentUser) return [];
    if (currentUser.is_admin) return users;
    // For non-admins, they can only filter their own tasks
    return [currentUser];
  };

  const visibleUsers = getVisibleUsers();

  const filteredTasks = tasks.filter(task => {
    // Status filter
    if (filter !== 'all' && task.status !== filter) return false;
    
    // User filter
    if (userFilter !== 'all' && task.assignee !== userFilter) return false;
    
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t('tasks')}</h1>
          <p className="text-muted-foreground mt-1">
            {language === 'hi' ? 'अपने सभी टास्क यहां देखें और प्रबंधित करें' : 'View and manage all your tasks here'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* User Filter (Admin only) */}
          {currentUser?.is_admin && visibleUsers.length > 0 && (
            <Select value={userFilter} onValueChange={setUserFilter}>
              <SelectTrigger className="w-[180px] bg-secondary/50 border-white/10">
                <Users className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by user" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'hi' ? 'सभी उपयोगकर्ता' : 'All Users'}</SelectItem>
                {visibleUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          
          {/* Status Filter */}
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[150px] bg-secondary/50 border-white/10">
              <Filter className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'hi' ? 'सभी' : 'All Status'}</SelectItem>
              <SelectItem value="todo">{t('todo')}</SelectItem>
              <SelectItem value="in_progress">{t('in_progress')}</SelectItem>
              <SelectItem value="done">{t('done')}</SelectItem>
            </SelectContent>
          </Select>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="glow-primary"
            data-testid="create-task-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('new_task')}
          </Button>
        </div>
      </div>

      {/* Tasks List */}
      <div className="space-y-3">
        {filteredTasks.length === 0 ? (
          <Card className="glass">
            <CardContent className="py-16 text-center">
              <CheckCircle2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold mb-2">
                {language === 'hi' ? 'कोई टास्क नहीं' : 'No tasks yet'}
              </h3>
              <p className="text-muted-foreground">
                {language === 'hi' ? 'नया टास्क बनाने के लिए बटन पर क्लिक करें' : 'Click the button above to create a new task'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredTasks.map((task, index) => (
            <motion.div
              key={task.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="glass card-hover" data-testid={`task-item-${task.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <button
                        onClick={() => handleStatusChange(task.id, task.status === 'done' ? 'todo' : 'done')}
                        className={`mt-1 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                          task.status === 'done'
                            ? 'bg-chart-3 border-chart-3'
                            : 'border-muted-foreground hover:border-primary'
                        }`}
                      >
                        {task.status === 'done' && (
                          <CheckCircle2 className="h-3 w-3 text-white" />
                        )}
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${task.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>
                          {task.title}
                        </p>
                        {task.description && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {task.description}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-3 mt-3 flex-wrap">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            task.priority === 'high' ? 'bg-destructive/20 text-destructive' :
                            task.priority === 'medium' ? 'bg-chart-4/20 text-chart-4' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {t(task.priority)}
                          </span>
                          
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            task.status === 'done' ? 'bg-chart-3/20 text-chart-3' :
                            task.status === 'in_progress' ? 'bg-chart-4/20 text-chart-4' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {t(task.status)}
                          </span>
                          
                          {task.due_date && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {new Date(task.due_date).toLocaleDateString()}
                            </span>
                          )}
                          
                          {task.tags?.map((tag, i) => (
                            <span key={i} className="text-xs px-2 py-1 rounded-full bg-primary/20 text-primary">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenTaskDetail(task)}>
                          <MessageSquare className="h-4 w-4 mr-2" />
                          {language === 'hi' ? 'विवरण देखें' : 'View Details'}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEditTask(task)}>
                          <Edit2 className="h-4 w-4 mr-2" />
                          {language === 'hi' ? 'संपादित करें' : 'Edit'}
                        </DropdownMenuItem>
                        {task.status !== 'todo' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'todo')}>
                            {t('todo')}
                          </DropdownMenuItem>
                        )}
                        {task.status !== 'in_progress' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'in_progress')}>
                            {t('in_progress')}
                          </DropdownMenuItem>
                        )}
                        {task.status !== 'done' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(task.id, 'done')}>
                            {t('done')}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleDeleteTask(task.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Create Task Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="glass border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('new_task')}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('task_title')}</Label>
              <div className="flex gap-2">
                <Input
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  placeholder={language === 'hi' ? 'टास्क का टाइटल' : 'Task title'}
                  className="bg-secondary/50 border-white/10"
                  data-testid="task-title-input"
                />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleAutoFill}
                  disabled={isAutoFilling || !newTask.title.trim()}
                  className="flex-shrink-0"
                  data-testid="ai-autofill-btn"
                >
                  {isAutoFilling ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {aiSuggestions && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl bg-primary/10 border border-primary/30"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    {t('ai_suggestions')}
                  </span>
                  <Button size="sm" onClick={applyAiSuggestions} data-testid="apply-suggestions-btn">
                    {t('apply_suggestions')}
                  </Button>
                </div>
                <div className="space-y-2 text-sm">
                  {aiSuggestions.description && (
                    <p><span className="text-muted-foreground">{t('task_description')}:</span> {aiSuggestions.description}</p>
                  )}
                  {aiSuggestions.priority && (
                    <p><span className="text-muted-foreground">{t('priority')}:</span> {t(aiSuggestions.priority)}</p>
                  )}
                  {aiSuggestions.tags?.length > 0 && (
                    <p><span className="text-muted-foreground">{t('tags')}:</span> {aiSuggestions.tags.join(', ')}</p>
                  )}
                </div>
              </motion.div>
            )}

            <div className="space-y-2">
              <Label>{t('project_name')}</Label>
              <Select
                value={newTask.project_id}
                onValueChange={(value) => setNewTask({ ...newTask, project_id: value })}
              >
                <SelectTrigger className="bg-secondary/50 border-white/10" data-testid="project-select">
                  <SelectValue placeholder={language === 'hi' ? 'प्रोजेक्ट चुनें' : 'Select project'} />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assignee Selection */}
            <div className="space-y-2">
              <Label>{language === 'hi' ? 'असाइनी' : 'Assign To'}</Label>
              <div className="flex gap-2">
                <Select
                  value={assigneeType}
                  onValueChange={(value) => {
                    setAssigneeType(value);
                    if (value === 'self') {
                      setNewTask({ ...newTask, assignee: currentUser?.id });
                    }
                  }}
                >
                  <SelectTrigger className="bg-secondary/50 border-white/10 w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="self">{language === 'hi' ? 'खुद' : 'Self'}</SelectItem>
                    {canAssignToOthers() && (
                      <SelectItem value="others">{language === 'hi' ? 'अन्य' : 'Others'}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                
                {assigneeType === 'others' && canAssignToOthers() && (
                  <Select
                    value={newTask.assignee || ''}
                    onValueChange={(value) => setNewTask({ ...newTask, assignee: value })}
                  >
                    <SelectTrigger className="bg-secondary/50 border-white/10 flex-1">
                      <SelectValue placeholder={language === 'hi' ? 'उपयोगकर्ता चुनें' : 'Select user'} />
                    </SelectTrigger>
                    <SelectContent>
                      {users.filter(u => u.id !== currentUser?.id).map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name} ({user.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              {!canAssignToOthers() && (
                <p className="text-xs text-muted-foreground">
                  {language === 'hi' ? 'योगदानकर्ता केवल अपने लिए टास्क बना सकते हैं' : 'Contributors can only create tasks for themselves'}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('task_description')}</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder={language === 'hi' ? 'विवरण (वैकल्पिक)' : 'Description (optional)'}
                className="bg-secondary/50 border-white/10"
                data-testid="task-desc-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('priority')}</Label>
                <Select
                  value={newTask.priority}
                  onValueChange={(value) => setNewTask({ ...newTask, priority: value })}
                >
                  <SelectTrigger className="bg-secondary/50 border-white/10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t('low')}</SelectItem>
                    <SelectItem value="medium">{t('medium')}</SelectItem>
                    <SelectItem value="high">{t('high')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t('due_date')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-secondary/50 border-white/10"
                    >
                      <Clock className="mr-2 h-4 w-4" />
                      {newTask.due_date ? format(new Date(newTask.due_date), 'PP') : (
                        language === 'hi' ? 'तारीख चुनें' : 'Pick a date'
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={newTask.due_date ? new Date(newTask.due_date) : undefined}
                      onSelect={(date) => setNewTask({ ...newTask, due_date: date?.toISOString() })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCreateDialog(false)}>
              {t('cancel')}
            </Button>
            <Button
              onClick={handleCreateTask}
              disabled={isCreating}
              className="glow-primary"
              data-testid="submit-task-btn"
            >
              {isCreating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t('create_task')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        task={selectedTask}
        open={showTaskDetail}
        onOpenChange={setShowTaskDetail}
        onSave={handleTaskDetailSave}
      />

      {/* Edit Task Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="glass border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {language === 'hi' ? 'टास्क संपादित करें' : 'Edit Task'}
            </DialogTitle>
          </DialogHeader>
          
          {editingTask && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>{t('title')}</Label>
                <Input
                  value={editingTask.title}
                  onChange={(e) => setEditingTask({ ...editingTask, title: e.target.value })}
                  className="bg-secondary/50 border-white/10"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('description')}</Label>
                <Textarea
                  value={editingTask.description || ''}
                  onChange={(e) => setEditingTask({ ...editingTask, description: e.target.value })}
                  className="bg-secondary/50 border-white/10"
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('priority')}</Label>
                  <Select
                    value={editingTask.priority}
                    onValueChange={(value) => setEditingTask({ ...editingTask, priority: value })}
                  >
                    <SelectTrigger className="bg-secondary/50 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">{t('low')}</SelectItem>
                      <SelectItem value="medium">{t('medium')}</SelectItem>
                      <SelectItem value="high">{t('high')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('status')}</Label>
                  <Select
                    value={editingTask.status}
                    onValueChange={(value) => setEditingTask({ ...editingTask, status: value })}
                  >
                    <SelectTrigger className="bg-secondary/50 border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todo">{t('todo')}</SelectItem>
                      <SelectItem value="in_progress">{t('in_progress')}</SelectItem>
                      <SelectItem value="done">{t('done')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{t('due_date')}</Label>
                <Input
                  type="date"
                  value={editingTask.due_date ? editingTask.due_date.split('T')[0] : ''}
                  onChange={(e) => setEditingTask({ ...editingTask, due_date: e.target.value })}
                  className="bg-secondary/50 border-white/10"
                />
              </div>
              
              {/* Reassign Task - Only for Admin/Lead/Manager */}
              {canReassignTask() && (
                <div className="space-y-2">
                  <Label>{language === 'hi' ? 'पुनः असाइन करें' : 'Reassign To'}</Label>
                  <Select
                    value={editingTask.assignee || ''}
                    onValueChange={(value) => setEditingTask({ ...editingTask, assignee: value })}
                  >
                    <SelectTrigger className="bg-secondary/50 border-white/10">
                      <SelectValue placeholder={language === 'hi' ? 'उपयोगकर्ता चुनें' : 'Select user'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={currentUser?.id}>{currentUser?.name} (Self)</SelectItem>
                      {users.filter(u => u.id !== currentUser?.id).map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEditDialog(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleSaveEdit} className="glow-primary">
              {language === 'hi' ? 'सहेजें' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPage;
